import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, List,
  MenuItem, FormControl, InputLabel, Select, Divider, Chip,
  Tabs, Tab, Switch, Slider, ToggleButton, ToggleButtonGroup,
  CircularProgress, FormControlLabel, Checkbox, Dialog,
  DialogTitle, DialogContent, DialogActions, Paper,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  CallSplit as CallSplitIcon, Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon, Check as CheckMarkIcon,
  Clear as CloseMarkIcon, Image as ImageIcon, TextFields as TextIcon,
  Title as TitleIcon, VideoLibrary as VideoIcon,
  SmartButton as ButtonIcon, CloudUpload as UploadIcon,
  FormatListBulleted as ListIcon, Star as StarIcon,
  Info as InfoIcon, Warning as WarningIcon, Verified as VerifiedIcon,
  ShoppingCart as ShoppingCartIcon, FileCopy as DuplicateIcon,
  ExpandMore as ExpandMoreIcon, ViewModule as TemplateIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getSuggestedTextColors } from '../../../../utils/offerColorUtils';
import { ELEMENT_TYPES, TEMPLATE_TYPE_OPTIONS } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import S3FileUpload from '../../../S3FileUpload';
import SimplePresetSelector from '../../../SimplePresetSelector';
import ButtonPresetSelector from '../../../ButtonPresetSelector';
import ComparisonTablePresetSelector from '../../../ComparisonTablePresetSelector';
import {
  BRANDS_EDITOR_RIGHT_PANEL_WIDTH, BRANDS_EDITOR_SAVED_COL_WIDTH,
  BRANDS_PAGE_MAX_HEIGHT, BRANDS_REF_DEVICE_WIDTH, BRANDS_REF_DEVICE_HEIGHT,
  SETUPS_REF_DEVICE_WIDTH, SETUPS_REF_DEVICE_HEIGHT,
} from '../../../../utils/offerDeviceConstants';
import { useOfferBuilder } from '../../OfferBuilderContext';

const StyleTab = () => {
  const {
      isMobile, isBrandsPage, templatesOnly, customElementsOnly,
      paginationEnabled, isEditingMode, forceRender,
      selectedElement, selectedElementContext, selectedSubElement,
      setSelectedElement, setSelectedElementContext, setSelectedSubElement,
      tabValue, setTabValue, rightPanelOpen, setRightPanelOpen,
      elements, pages, currentPageIndex, pageSettings,
      updateElement, addElement, moveToNewPage,
      handleImageUpload, handleThumbnailUpload, handleMediaUploadForElement,
      contentTabTempValue, setContentTabTempValue,
      buttonContentTabTempValue, setButtonContentTabTempValue,
      buttonUrlTabTempValue, setButtonUrlTabTempValue,
      saveContentTabEdit, cancelContentTabEdit, handleContentTabKeyPress,
      saveButtonContentTabEdit, cancelButtonContentTabEdit, handleButtonContentTabKeyPress,
      saveButtonUrlTabEdit, cancelButtonUrlTabEdit, handleButtonUrlTabKeyPress,
      listItemTextValues, setListItemTextValues,
      localRatingText, setLocalRatingText,
      renameElementId, setRenameElementId, renameElementValue, setRenameElementValue,
      getCurrentFontFamily, getInlineBackgroundColors, getInlineTextColors,
      savedOfferElements, filteredSavedElements, setFilteredSavedElements,
      loadSavedOfferElements, loadSavedElement, deleteSavedElement, editSavedElement,
      selectedBrand, selectedOffer, brands, offers, setSelectedBrand, setSelectedOffer, loadOffersForBrand,
      setShowStatusAlert, setStatusMessage, setStatusTitle,
      showConfirmDialog, setShowConfirmDialog, confirmDialogTitle, confirmDialogMessage,
      confirmAction, setConfirmAction, setConfirmDialogTitle, setConfirmDialogMessage,
      showRenameDialog, setShowRenameDialog,
      showLayoutSelector, setShowLayoutSelector, targetZone, setTargetZone,
      showSaveElementDialog, setShowSaveElementDialog, isUpdatingElement, setIsUpdatingElement,
      currentElementName, setCurrentElementName, selectedSavedElementId, setSelectedSavedElementId,
      setSaveType, saveType, saveCurrentElement,
      showLoadTemplateAlert, setShowLoadTemplateAlert, loadTemplateError,
      showReplaceConfirm, setShowReplaceConfirm, pendingTemplateData, loadReplaceTemplate,
      showStatusAlert, statusMessage, statusTitle,
      showPromptDialog, setShowPromptDialog, promptDialogTitle, promptDialogMessage,
      promptDialogValue, setPromptDialogValue, promptAction, setPromptAction,
      showSaveDialog, setShowSaveDialog, saveTemplate,
      showTemplatesList, setShowTemplatesList, templates, loadTemplate, deleteTemplate,
      setOffers, setForceRender,
      imageUploading, uploadingElementId, savedTemplates, setSavedTemplates,
      preBuiltTemplates, loadPreBuiltTemplate, currentTemplateId, setCurrentTemplateId,
      templateName, setTemplateName, templateType, setTemplateType,
      templateSubtype, setTemplateSubtype, groupTemplatesByType, addLayout,
      showBrandOfferAlert, setShowBrandOfferAlert, renameSavedElement,
      setCurrentPageIndex, setElements, setNextPageId, setPageSettings,
      setPages, setPaginationEnabled, setPendingTemplateData,
    } = useOfferBuilder();

  return (
    <Box>
      {/* Header with Title */}
      <Box sx={{
        mb: 2,
        pb: 1.5,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="subtitle1" sx={{ mb: 0 }}>
          🎨 Styling Options
        </Typography>
      </Box>
    
      {/* Custom List Text Style Presets */}
      {selectedElement.type === ELEMENT_TYPES.CUSTOM_LIST && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎨 List Item Text Style
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a preset style that applies to all list items
          </Typography>
    
          <SimplePresetSelector
            onStyleSelect={(presetName, styles) => {
              console.log('🎨 Custom list preset selected:', presetName, styles);
              updateElement(selectedElement.id, { textStylePreset: presetName });
            }}
            currentStyle={selectedElement.textStylePreset}
            presetFilter={(presetName) => {
              // Only show presets that text elements have (exclude quotation and bullet presets)
              return !presetName.includes('quotation') &&
                !presetName.includes('bullet');
            }}
          />
        </Box>
      )}
    
      {/* Custom List Background Color Settings */}
      {selectedElement.type === ELEMENT_TYPES.CUSTOM_LIST && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎨 Background Color
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a preset color or enter a custom value
          </Typography>
    
          {/* Enhanced Color Palette */}
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${isBrandsPage ? 4 : 5}, 1fr)`, gap: 1, mb: 2 }}>
            {getInlineBackgroundColors().map((color) => {
              const currentBgColor = selectedElement.styles?.backgroundColor || 'transparent';
              const isSelected = currentBgColor === color.value;
    
              return (
                <Box
                  key={color.name}
                  onClick={() => {
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, backgroundColor: color.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }}
                  sx={{
                    width: '100%',
                    height: '32px',
                    backgroundColor: color.value === 'transparent' ? '#f5f5f5' : color.value,
                    border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.05)'
                    },
                    ...(color.value === 'transparent' && {
                      backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                      backgroundSize: '8px 8px',
                      backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                    })
                  }}
                  title={`${color.name} (${color.value})`}
                />
              );
            })}
          </Box>
    
          {/* Custom color input - Hide in Brands page */}
          {!isBrandsPage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <input
                type="color"
                value={(() => {
                  const bgColor = selectedElement.styles?.backgroundColor || 'transparent';
                  if (bgColor === 'transparent' || bgColor === 'none') return '#ffffff';
                  if (bgColor.startsWith('#')) return bgColor;
                  return '#ffffff';
                })()}
                onChange={(e) => {
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, backgroundColor: e.target.value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }}
                style={{
                  width: '50px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Pick a color"
              />
              <TextField
                fullWidth
                label="Custom Background Color"
                value={selectedElement.styles?.backgroundColor || 'transparent'}
                onChange={(e) => {
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, backgroundColor: e.target.value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }}
                placeholder="e.g., #181818, rgb(24,24,24), or transparent"
                helperText="Enter any valid CSS color value"
              />
            </Box>
          )}
        </Box>
      )}
    
      {/* Custom List Text Transform Settings */}
      {selectedElement.type === ELEMENT_TYPES.CUSTOM_LIST && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🔤 Text Transform
          </Typography>
    
          <TextField
            fullWidth
            label="Text Transform"
            select
            value={selectedElement.styles?.textTransform || 'none'}
            onChange={e => updateElement(selectedElement.id, { styles: { ...selectedElement.styles, textTransform: e.target.value } })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="none">Normal</MenuItem>
            <MenuItem value="uppercase">ALL CAPITAL</MenuItem>
            <MenuItem value="capitalize">First Letter Capital</MenuItem>
            <MenuItem value="titlecase">Title Case</MenuItem>
            <MenuItem value="lowercase">all lowercase</MenuItem>
          </TextField>
        </Box>
      )}
    
      {/* Custom List Text Color Settings */}
      {selectedElement.type === ELEMENT_TYPES.CUSTOM_LIST && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎨 Text Color
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a preset color or enter a custom value
          </Typography>
    
          {/* Text Color Palette */}
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${isBrandsPage ? 4 : 5}, 1fr)`, gap: 1, mb: 2 }}>
            {getInlineTextColors().map((color) => {
              const currentTextColor = selectedElement.styles?.color || '#ffffff';
              const isSelected = currentTextColor === color.value;
    
              return (
                <Box
                  key={color.name}
                  onClick={() => {
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, color: color.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }}
                  sx={{
                    width: '100%',
                    height: '32px',
                    backgroundColor: color.value,
                    border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.05)'
                    }
                  }}
                  title={`${color.name} (${color.value})`}
                />
              );
            })}
          </Box>
    
          {/* Custom text color input - Hide in Brands page */}
          {!isBrandsPage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <input
                type="color"
                value={(() => {
                  const textColor = selectedElement.styles?.color || '#ffffff';
                  if (textColor.startsWith('#')) return textColor;
                  return '#ffffff';
                })()}
                onChange={(e) => {
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, color: e.target.value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }}
                style={{
                  width: '50px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Pick a color"
              />
              <TextField
                fullWidth
                label="Custom Text Color"
                value={selectedElement.styles?.color || '#ffffff'}
                onChange={(e) => {
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, color: e.target.value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }}
                placeholder="e.g., #ffffff, rgb(255,255,255), or white"
                helperText="Enter any valid CSS color value"
              />
            </Box>
          )}
        </Box>
      )}
    
      {/* Two-Line Custom List Text Style Presets */}
      {selectedElement.type === ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE && (
        <>
          {/* First Line (Title) Preset Selector */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🎯 Title Text Style (Line 1)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for feature titles
            </Typography>
    
            <SimplePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Two-line list title preset selected:', presetName, styles);
                updateElement(selectedElement.id, { textStylePreset1: presetName });
              }}
              currentStyle={selectedElement.textStylePreset1}
              presetFilter={(presetName) => {
                // Only show presets that text elements have (exclude quotation and bullet presets)
                return !presetName.includes('quotation') &&
                  !presetName.includes('bullet');
              }}
            />
          </Box>
    
          {/* Second Line (Description) Preset Selector */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f0f9ff' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              📝 Description Text Style (Line 2)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for feature descriptions
            </Typography>
    
            <SimplePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Two-line list description preset selected:', presetName, styles);
                updateElement(selectedElement.id, { textStylePreset2: presetName });
              }}
              currentStyle={selectedElement.textStylePreset2}
              presetFilter={(presetName) => {
                // Only show presets that text elements have (exclude quotation and bullet presets)
                return !presetName.includes('quotation') &&
                  !presetName.includes('bullet');
              }}
            />
          </Box>
        </>
      )}
    
      {/* Preset Text Styles - Show for text-based elements except quotation blocks and buttons */}
      {(selectedElement.type === ELEMENT_TYPES.TEXT ||
        selectedElement.type === ELEMENT_TYPES.HEADING ||
        selectedElement.type === ELEMENT_TYPES.TEXT_WITH_RIGHT_IMAGE ||
        selectedElement.type === ELEMENT_TYPES.LEFT_IMAGE_TEXT ||
        selectedElement.type === 'text' ||
        selectedElement.type === 'heading') && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🎨 Preset Text Styles
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Click any style to quickly apply a complete typography set.
            </Typography>
    
            <SimplePresetSelector
              onStyleSelect={(presetName, preset) => {
                console.log('🎨 Preset applied:', presetName, preset);
                console.log('🎯 Current selectedElement:', selectedElement);
                console.log('🎨 Preset fontFamily:', preset.fontFamily);
                console.log('🎨 Preset fontSize:', preset.fontSize);
    
                // List of preset-related properties that should be completely replaced
                const presetProperties = [
                  'fontFamily',
                  'fontSize',
                  'fontWeight',
                  'lineHeight',
                  'letterSpacing',
                  'color',
                  'textAlign',
                  'textTransform',
                  'fontStyle'
                ];
    
                // Different elements store styles in different places
                if (selectedElement.type === ELEMENT_TYPES.TEXT ||
                  selectedElement.type === ELEMENT_TYPES.HEADING ||
                  selectedElement.type === ELEMENT_TYPES.BUTTON ||
                  selectedElement.type === 'text' ||
                  selectedElement.type === 'heading' ||
                  selectedElement.type === 'button') {
                  // These elements use 'styles' property
                  // Start with existing styles, but remove all preset-related properties
                  const existingStyles = selectedElement.styles || {};
                  const nonPresetStyles = {};
    
                  // Copy only non-preset properties from existing styles
                  Object.keys(existingStyles).forEach(key => {
                    if (!presetProperties.includes(key) && key !== 'presetStyle') {
                      nonPresetStyles[key] = existingStyles[key];
                    }
                  });
    
                  // Build new styles: non-preset properties + preset properties + presetStyle
                  const newStyles = {
                    ...nonPresetStyles,
                    ...preset,
                    presetStyle: presetName
                  };
    
                  // Explicitly remove textTransform if not in preset (to clear uppercase from previous preset)
                  if (!preset.hasOwnProperty('textTransform')) {
                    newStyles.textTransform = 'none';
                  }
    
                  console.log('📝 Previous styles:', existingStyles);
                  console.log('📝 New styles for main element:', newStyles);
    
                  // Check if element is in a zone
                  if (selectedElementContext?.isInZone && selectedElementContext?.layoutId && selectedElementContext?.zoneId) {
                    updateElement(selectedElement.id, {
                      styles: newStyles
                    }, true, selectedElementContext.layoutId, selectedElementContext.zoneId);
                  } else {
                    updateElement(selectedElement.id, {
                      styles: newStyles
                    });
                  }
    
                  // Debug: Check if selectedElement updates after a delay
                  setTimeout(() => {
                    console.log('⏰ Delayed check - selectedElement styles:', selectedElement?.styles);
                    console.log('⏰ Delayed check - selectedElement fontFamily:', selectedElement?.styles?.fontFamily);
                    console.log('⏰ Delayed check - selectedElement fontSize:', selectedElement?.styles?.fontSize);
                    console.log('⏰ Delayed check - selectedElement textTransform:', selectedElement?.styles?.textTransform);
                  }, 100);
                } else {
                  // Other elements like TEXT_WITH_RIGHT_IMAGE use 'textStyles' property
                  // Start with existing textStyles, but remove all preset-related properties
                  const existingTextStyles = selectedElement.textStyles || {};
                  const nonPresetTextStyles = {};
    
                  // Copy only non-preset properties from existing textStyles
                  Object.keys(existingTextStyles).forEach(key => {
                    if (!presetProperties.includes(key) && key !== 'presetStyle') {
                      nonPresetTextStyles[key] = existingTextStyles[key];
                    }
                  });
    
                  // Build new textStyles: non-preset properties + preset properties + presetStyle
                  const newTextStyles = {
                    ...nonPresetTextStyles,
                    ...preset,
                    presetStyle: presetName
                  };
    
                  // Explicitly remove textTransform if not in preset (to clear uppercase from previous preset)
                  if (!preset.hasOwnProperty('textTransform')) {
                    newTextStyles.textTransform = 'none';
                  }
    
                  console.log('📝 Previous textStyles:', existingTextStyles);
                  console.log('📝 New textStyles for composite element:', newTextStyles);
    
                  // Check if element is in a zone
                  if (selectedElementContext?.isInZone && selectedElementContext?.layoutId && selectedElementContext?.zoneId) {
                    updateElement(selectedElement.id, {
                      textStyles: newTextStyles
                    }, true, selectedElementContext.layoutId, selectedElementContext.zoneId);
                  } else {
                    updateElement(selectedElement.id, {
                      textStyles: newTextStyles
                    });
                  }
    
                  // Debug: Check if selectedElement updates after a delay
                  setTimeout(() => {
                    console.log('⏰ Delayed check - selectedElement textStyles:', selectedElement?.textStyles);
                    console.log('⏰ Delayed check - selectedElement textTransform:', selectedElement?.textStyles?.textTransform);
                  }, 100);
                }
              }}
              currentStyle={
                selectedElement.textStyles?.presetStyle ||
                selectedElement.styles?.presetStyle ||
                selectedElement.textStylePreset
              }
              presetFilter={(presetName) => !presetName.startsWith('quotation') && !presetName.startsWith('bullet')}
            />
          </Box>
        )}
    
      {/* Preset Button Styles - Show for button-based elements */}
      {(selectedElement.type === ELEMENT_TYPES.BUTTON ||
        selectedElement.type === 'button' ||
        selectedElement.type === 'BUTTON_TEXT_VARIANT' ||
        selectedElement.type === 'BUTTON_IMAGE_CALLOUT' ||
        selectedElement.type === 'BUTTON_HEADLINE_PRODUCT_IMAGE' ||
        selectedElement.type === ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY) && (
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🎨 Preset Button Styles
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Click any style to quickly apply a complete button styling set.
            </Typography>
    
            <ButtonPresetSelector
              currentStyle={selectedElement?.styles?.presetStyle}
              onStyleSelect={(presetName, preset) => {
                console.log('🎨 Button preset applied:', presetName, preset);
                console.log('🎯 Current selectedElement:', selectedElement);
                console.log('🎨 Preset fontFamily:', preset.fontFamily);
                console.log('🎨 Preset fontSize:', preset.fontSize);
                console.log('🎨 Preset textTransform:', preset.textTransform);
                console.log('🎨 Preset backgroundColor:', preset.backgroundColor);
                console.log('🎨 Preset color:', preset.color);
    
                // Button elements use 'styles' property
                // Completely replace styles instead of merging to avoid conflicts
    
                // Separate styles from icon properties
                const { iconLeft, iconRight, ...styleProperties } = preset;
    
                // For VIDEO_WITH_BUTTON_OVERLAY, we want to preserve video-related styles
                // while applying the button content styles
                let newStyles;
                if (selectedElement.type === ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY) {
                  const videoStyles = {};
                  // Keep existing video frame properties if they exist
                  if (selectedElement.styles?.width) videoStyles.width = selectedElement.styles.width;
                  if (selectedElement.styles?.height) videoStyles.height = selectedElement.styles.height;
                  if (selectedElement.styles?.aspectRatio) videoStyles.aspectRatio = selectedElement.styles.aspectRatio;
                  if (selectedElement.styles?.marginBottom) videoStyles.marginBottom = selectedElement.styles.marginBottom;
    
                  newStyles = {
                    ...styleProperties,
                    ...videoStyles,
                    presetStyle: presetName
                  };
                } else {
                  // Start with a completely clean slate - no previous styles
                  newStyles = {
                    ...styleProperties,
                    presetStyle: presetName
                  };
                }
    
                console.log('🧹 Clearing all previous styles and applying preset:', presetName);
                console.log('🧹 Previous styles:', selectedElement.styles);
                console.log('🧹 New styles:', newStyles);
    
                console.log('📝 New styles for button element:', newStyles);
                console.log('📝 New textTransform:', newStyles.textTransform);
                console.log('📝 New backgroundColor:', newStyles.backgroundColor);
                console.log('📝 New boxShadow:', newStyles.boxShadow);
                console.log('📝 Icon properties:', { iconLeft, iconRight });
    
                // Prepare element update with both styles and icon properties
                const elementUpdate = {
                  styles: newStyles
                };
    
                // Always explicitly set icon properties to clear any previous icons
                elementUpdate.iconLeft = iconLeft || null;
                elementUpdate.iconRight = iconRight || null;
    
                // Check if element is in a zone
                if (selectedElementContext?.isInZone && selectedElementContext?.layoutId && selectedElementContext?.zoneId) {
                  updateElement(selectedElement.id, elementUpdate, true, selectedElementContext.layoutId, selectedElementContext.zoneId);
                } else {
                  updateElement(selectedElement.id, elementUpdate);
                }
    
                // Force a re-render to see the changes immediately
                setForceRender(prev => prev + 1);
    
                // Log the final result after update
                setTimeout(() => {
                  console.log('🎯 Final selectedElement after update:', selectedElement);
                  console.log('🎨 Final styles:', selectedElement?.styles);
                }, 100);
    
                // Debug: Check if selectedElement updates after a delay
                setTimeout(() => {
                  console.log('⏰ Delayed check - selectedElement styles:', selectedElement?.styles);
                  console.log('⏰ Delayed check - selectedElement fontFamily:', selectedElement?.styles?.fontFamily);
                  console.log('⏰ Delayed check - selectedElement fontSize:', selectedElement?.styles?.fontSize);
                  console.log('⏰ Delayed check - selectedElement textTransform:', selectedElement?.styles?.textTransform);
                }, 100);
              }}
            />
          </Box>
        )}
    
      {/* Preset Button Text Styles */}
      {selectedElement.type === ELEMENT_TYPES.BUTTON && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            📝 Preset Button Text Styles
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Apply text styling presets to button text content.
          </Typography>
    
          <SimplePresetSelector
            currentStyle={selectedElement?.styles?.textPresetStyle}
            presetFilter={(presetName) => {
              // Filter out quotation and bullet-related presets for button text
              return !presetName.includes('quotation') &&
                !presetName.includes('bullet');
            }}
            onStyleSelect={(presetName, preset) => {
              console.log('📝 Button text preset applied:', presetName, preset);
              console.log('🎯 Current selectedElement:', selectedElement);
              console.log('📝 Preset fontFamily:', preset.fontFamily);
              console.log('📝 Preset fontSize:', preset.fontSize);
              console.log('📝 Preset fontWeight:', preset.fontWeight);
              console.log('📝 Preset color:', preset.color);
              console.log('📝 Preset textAlign:', preset.textAlign);
              console.log('📝 Preset textTransform:', preset.textTransform);
    
              // List of preset-related properties that should be completely replaced
              const presetProperties = [
                'fontFamily',
                'fontSize',
                'fontWeight',
                'lineHeight',
                'letterSpacing',
                'color',
                'textAlign',
                'textTransform',
                'fontStyle'
              ];
    
              // Start with existing styles, but remove all preset-related properties
              const existingStyles = selectedElement.styles || {};
              const nonPresetStyles = {};
    
              // Copy only non-preset properties from existing styles (preserve button-specific properties)
              Object.keys(existingStyles).forEach(key => {
                if (!presetProperties.includes(key) && key !== 'presetStyle' && key !== 'textPresetStyle') {
                  nonPresetStyles[key] = existingStyles[key];
                }
              });
    
              // Build new styles: non-preset properties + preset properties + textPresetStyle
              const newStyles = {
                ...nonPresetStyles,
                ...preset,
                textPresetStyle: presetName
              };
    
              // Explicitly remove textTransform if not in preset (to clear uppercase from previous preset)
              if (!preset.hasOwnProperty('textTransform')) {
                newStyles.textTransform = 'none';
              }
    
              console.log('📝 Previous styles:', existingStyles);
              console.log('📝 New styles:', newStyles);
    
              // Check if element is in a zone
              if (selectedElementContext?.isInZone && selectedElementContext?.layoutId && selectedElementContext?.zoneId) {
                updateElement(selectedElement.id, { styles: newStyles }, true, selectedElementContext.layoutId, selectedElementContext.zoneId);
              } else {
                updateElement(selectedElement.id, { styles: newStyles });
              }
    
              // Force a re-render to see the changes immediately
              setForceRender(prev => prev + 1);
    
              // Log the final result after update
              setTimeout(() => {
                console.log('🎯 Final selectedElement after text preset update:', selectedElement);
                console.log('📝 Final text styles:', selectedElement?.styles);
                console.log('📝 Final textTransform:', selectedElement?.styles?.textTransform);
              }, 100);
            }}
          />
        </Box>
      )}
    
      {/* Quotation Block Specific Presets */}
      {selectedElement.type === ELEMENT_TYPES.QUOTATION_BLOCK && (
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: '#0369a1' }}>
            💬 Quotation Style Presets
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Quick styles designed specifically for quotation blocks.
          </Typography>
          <SimplePresetSelector
            onStyleSelect={(presetName, preset) => {
              console.log('🎨 Quotation preset applied:', presetName, preset);
    
              // List of preset-related properties that should be completely replaced
              const presetProperties = [
                'fontFamily',
                'fontSize',
                'fontWeight',
                'lineHeight',
                'letterSpacing',
                'color',
                'textAlign',
                'textTransform',
                'fontStyle'
              ];
    
              // Start with existing styles, but remove all preset-related properties
              const existingStyles = selectedElement.styles || {};
              const nonPresetStyles = {};
    
              // Copy only non-preset properties from existing styles
              Object.keys(existingStyles).forEach(key => {
                if (!presetProperties.includes(key) && key !== 'presetStyle' && key !== 'textStylePreset') {
                  nonPresetStyles[key] = existingStyles[key];
                }
              });
    
              // Build new styles: non-preset properties + preset properties + textStylePreset
              const newStyles = {
                ...nonPresetStyles,
                ...preset,
                textStylePreset: presetName
              };
    
              // Explicitly remove textTransform if not in preset (to clear uppercase from previous preset)
              if (!preset.hasOwnProperty('textTransform')) {
                newStyles.textTransform = 'none';
              }
    
              console.log('📝 Previous styles:', existingStyles);
              console.log('📝 New styles:', newStyles);
    
              // Check if element is in a zone
              if (selectedElementContext?.isInZone && selectedElementContext?.layoutId && selectedElementContext?.zoneId) {
                updateElement(selectedElement.id, {
                  styles: newStyles,
                  textStylePreset: presetName
                }, true, selectedElementContext.layoutId, selectedElementContext.zoneId);
              } else {
                updateElement(selectedElement.id, {
                  styles: newStyles,
                  textStylePreset: presetName
                });
              }
            }}
            currentStyle={selectedElement.textStylePreset}
            presetFilter={(presetName) => presetName.startsWith('quotation')}
          />
        </Box>
      )}
    
      {/* Comparison Table Style Presets */}
      {selectedElement.type === ELEMENT_TYPES.COMPARISON_TABLE && (
        <>
          {/* Table Title Preset */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🏆 Table Title Style
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for the table title
            </Typography>
    
            <ComparisonTablePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Table title preset selected:', presetName, styles);
                updateElement(selectedElement.id, { titleStylePreset: presetName });
              }}
              currentStyle={selectedElement.titleStylePreset}
              presetFilter={(presetName) => presetName === 'compareTableTableTitle'}
            />
          </Box>
    
          {/* Product Names Preset */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#e8f5e8' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🏷️ Product Names Style
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for product A and B names
            </Typography>
    
            <ComparisonTablePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Product names preset selected:', presetName, styles);
                updateElement(selectedElement.id, { productNameStylePreset: presetName });
              }}
              currentStyle={selectedElement.productNameStylePreset}
              presetFilter={(presetName) => presetName === 'compareTableProdctName'}
            />
          </Box>
    
          {/* Feature Title Preset */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff3e0' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              ⭐ Feature Titles Style
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for feature names
            </Typography>
    
            <ComparisonTablePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Feature title preset selected:', presetName, styles);
                updateElement(selectedElement.id, { featureTitleStylePreset: presetName });
              }}
              currentStyle={selectedElement.featureTitleStylePreset}
              presetFilter={(presetName) => presetName === 'compareTableFeatureTitle'}
            />
          </Box>
    
          {/* Feature Description Preset */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f0f9ff' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              📝 Feature Descriptions Style
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for feature descriptions
            </Typography>
    
            <ComparisonTablePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Feature description preset selected:', presetName, styles);
                updateElement(selectedElement.id, { featureDescStylePreset: presetName });
              }}
              currentStyle={selectedElement.featureDescStylePreset}
              presetFilter={(presetName) => presetName === 'compareTableFeatureDescription'}
            />
          </Box>
        </>
      )}
    
      {/* Two Column Comparison Style Presets */}
      {selectedElement.type === ELEMENT_TYPES.TWO_COLUMN_COMPARISON && (
        <>
          {/* Column Title Preset */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#e8f5e8' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              🏷️ Column Titles Style
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for left and right column titles
            </Typography>
    
            <ComparisonTablePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Column title preset selected:', presetName, styles);
                updateElement(selectedElement.id, { titleStylePreset: presetName });
              }}
              currentStyle={selectedElement.titleStylePreset}
              presetFilter={(presetName) => presetName === 'compareTableTwoColumnColumnTitle'}
            />
          </Box>
    
          {/* Item Text Preset */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fff3e0' }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              📝 Item Text Style
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset style for comparison items (positive and negative points)
            </Typography>
    
            <ComparisonTablePresetSelector
              onStyleSelect={(presetName, styles) => {
                console.log('🎨 Item text preset selected:', presetName, styles);
                updateElement(selectedElement.id, { itemStylePreset: presetName });
              }}
              currentStyle={selectedElement.itemStylePreset}
              presetFilter={(presetName) => presetName === 'compareTableTwoColumnItemTitle'}
            />
          </Box>
        </>
      )}
    
      {/* Style Variant Selector for BUTTON_WITH_NOTICE */}
      {selectedElement.type === 'BUTTON_WITH_NOTICE' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎨 Style Variants
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a style variation for the current template
          </Typography>
    
          {/* Style Variant Selector */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
            {(() => {
              const styleOptions = {
                'urgent-sale': ['default', 'modern', 'minimal', 'gradient'],
                'membership-quiz': ['default', 'elegant', 'soft', 'corporate'],
                'media-buzz': ['default', 'news', 'viral', 'trending']
              };
    
              return styleOptions[selectedElement.variant]?.map((styleVariant) => (
                <Chip
                  key={styleVariant}
                  label={styleVariant.charAt(0).toUpperCase() + styleVariant.slice(1)}
                  size="small"
                  variant={selectedElement.styleVariant === styleVariant ? 'filled' : 'outlined'}
                  onClick={() => {
                    const updatedElement = {
                      ...selectedElement,
                      styleVariant
                    };
                    updateElement(selectedElement.id, updatedElement, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }}
                  sx={{
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    backgroundColor: selectedElement.styleVariant === styleVariant ? '#1976d2' : '#ffffff',
                    color: selectedElement.styleVariant === styleVariant ? '#ffffff' : '#1976d2',
                    border: `1px solid #1976d2`,
                    '&:hover': {
                      backgroundColor: selectedElement.styleVariant === styleVariant ? '#1565c0' : '#e3f2fd'
                    }
                  }}
                />
              )) || [];
            })()}
          </Box>
        </Box>
      )}
    
    
    
      {/* Size Controls for Circular Image */}
      {selectedElement.type === ELEMENT_TYPES.CIRCULAR_IMAGE && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            📏 Size Settings
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Adjust the size of your circular image
          </Typography>
    
          {/* Size Presets */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Size Preset</InputLabel>
            <Select
              value={(() => {
                const currentSize = selectedElement.styles?.width || 200;
                const preset = [
                  { label: 'Small (100px)', value: 100 },
                  { label: 'Medium (150px)', value: 150 },
                  { label: 'Large (200px)', value: 200 },
                  { label: 'Extra Large (250px)', value: 250 },
                  { label: 'Custom', value: 'custom' }
                ].find(p => p.value === currentSize);
                return preset ? preset.value : 'custom';
              })()}
              label="Size Preset"
              onChange={(e) => {
                const newSize = e.target.value;
                const sizeValue = newSize === 'custom' ? (selectedElement.styles?.width || 200) : newSize;
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    width: sizeValue,
                    height: sizeValue
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
            >
              <MenuItem value={100}>Small (100px)</MenuItem>
              <MenuItem value={150}>Medium (150px)</MenuItem>
              <MenuItem value={200}>Large (200px)</MenuItem>
              <MenuItem value={250}>Extra Large (250px)</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
    
          {/* Custom Size Input */}
          <TextField
            fullWidth
            label="Custom Size (px)"
            type="number"
            value={selectedElement.styles?.width || 200}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              if (newSize >= 50 && newSize <= 500) {
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    width: newSize,
                    height: newSize
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }
            }}
            inputProps={{ min: 50, max: 500 }}
            sx={{ mb: 2 }}
          />
        </Box>
      )}
    
      {/* Frame Preset Options for Fittable Image */}
      {(selectedElement.type === ELEMENT_TYPES.FITTABLE_IMAGE || selectedElement.type === ELEMENT_TYPES.IMAGE_OVERLAY) && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🖼️ {selectedElement.type === ELEMENT_TYPES.IMAGE_OVERLAY ? 'Background Frame Presets' : 'Frame Presets'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a preset frame size for common aspect ratios
          </Typography>
    
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = baseWidth;
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    height: newHeight
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              Square (1:1)
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = Math.round(baseWidth * 9 / 16);
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    height: newHeight
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              16:9
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = Math.round(baseWidth * 16 / 9);
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    height: newHeight
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              9:16
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = Math.round(baseWidth * 5 / 4);
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    height: newHeight
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              4:5
            </Button>
          </Box>
    
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Current: {selectedElement.styles?.width === "100%" ? '100%' : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400)} × {selectedElement.styles?.height || 300}
          </Typography>
        </Box>
      )}
    
      {/* Frame Preset Options for Video */}
      {(selectedElement.type === ELEMENT_TYPES.VIDEO || selectedElement.type === ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY) && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎥 Video Frame Presets
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a preset frame size for common video aspect ratios{isBrandsPage ? ' (videos always display at full width)' : ''}
          </Typography>
    
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = baseWidth;
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    width: isBrandsPage ? '100%' : selectedElement.styles?.width,
                    height: newHeight,
                    aspectRatio: '1/1' // Set aspect ratio for square
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              Square (1:1)
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = Math.round(baseWidth * 9 / 16);
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    width: isBrandsPage ? '100%' : selectedElement.styles?.width,
                    height: newHeight,
                    aspectRatio: '16/9' // Set aspect ratio for landscape
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              16:9
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = Math.round(baseWidth * 16 / 9);
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    width: isBrandsPage ? '100%' : selectedElement.styles?.width,
                    height: newHeight,
                    aspectRatio: '9/16' // Set aspect ratio for portrait
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              9:16
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const baseWidth = selectedElement.styles?.width === "100%" ? 400 : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400);
                const newHeight = Math.round(baseWidth * 5 / 4);
                updateElement(selectedElement.id, {
                  styles: {
                    ...selectedElement.styles,
                    width: isBrandsPage ? '100%' : selectedElement.styles?.width,
                    height: newHeight,
                    aspectRatio: '4/5' // Set aspect ratio for 4:5
                  }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              sx={{ fontSize: '0.75rem' }}
            >
              4:5
            </Button>
          </Box>
    
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Current: {selectedElement.styles?.width === "100%" ? '100%' : (typeof selectedElement.styles?.width === 'number' ? selectedElement.styles.width : 400)} × {selectedElement.styles?.height || 300}
          </Typography>
        </Box>
      )}
    
    
      {/* Star Rating Table Style Controls */}
      {selectedElement.type === ELEMENT_TYPES.STAR_RATING_TABLE && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            ⭐ Star & Text Sizing
          </Typography>
    
          {/* Star Size */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Star Size: {selectedElement.styles?.fontSize || '22px'}
            </Typography>
            <Slider
              key={`star-size-${selectedElement.id}`}
              defaultValue={parseInt(selectedElement.styles?.fontSize) || 22}
              onChangeCommitted={(e, value) => {
                updateElement(selectedElement.id, {
                  styles: { ...selectedElement.styles, fontSize: `${value}px` }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              step={1}
              min={12}
              max={48}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
    
          {/* Label / Suffix Font Size */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Label / Suffix Font Size: {selectedElement.styles?.labelFontSize || '18px'}
            </Typography>
            <Slider
              key={`label-size-${selectedElement.id}`}
              defaultValue={parseInt(selectedElement.styles?.labelFontSize) || 18}
              onChangeCommitted={(e, value) => {
                updateElement(selectedElement.id, {
                  styles: { ...selectedElement.styles, labelFontSize: `${value}px` }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              step={1}
              min={10}
              max={36}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
    
          {/* Star Spacing (gap between individual stars) */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Star Spacing: {selectedElement.styles?.gap || '4px'}
            </Typography>
            <Slider
              key={`star-gap-${selectedElement.id}`}
              defaultValue={parseInt(selectedElement.styles?.gap) || 4}
              onChangeCommitted={(e, value) => {
                updateElement(selectedElement.id, {
                  styles: { ...selectedElement.styles, gap: `${value}px` }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              step={1}
              min={0}
              max={20}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
    
          {/* Title Font Size */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Title Font Size: {selectedElement.styles?.titleFontSize || '20px'}
            </Typography>
            <Slider
              key={`title-size-${selectedElement.id}`}
              defaultValue={parseInt(selectedElement.styles?.titleFontSize) || 20}
              onChangeCommitted={(e, value) => {
                updateElement(selectedElement.id, {
                  styles: { ...selectedElement.styles, titleFontSize: `${value}px` }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              step={1}
              min={12}
              max={36}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
    
          {/* Horizontal Alignment (for everything in row) */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Table Horizontal Alignment: {selectedElement.styles?.justifyContent || 'flex-start'}
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.styles?.justifyContent || 'flex-start'}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, justifyContent: value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="flex-start" sx={{ textTransform: 'none', py: 0.5 }}>Left</ToggleButton>
              <ToggleButton value="center" sx={{ textTransform: 'none', py: 0.5 }}>Center</ToggleButton>
              <ToggleButton value="flex-end" sx={{ textTransform: 'none', py: 0.5 }}>Right</ToggleButton>
            </ToggleButtonGroup>
          </Box>
    
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            📏 Row Layout & Alignment
          </Typography>
    
          {/* Row Layout Toggle */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Row Layout
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.rowLayout || 'stacked'}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  updateElement(selectedElement.id, { rowLayout: value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="stacked" sx={{ textTransform: 'none', py: 0.5 }}>Stacked (Above)</ToggleButton>
              <ToggleButton value="side-by-side" sx={{ textTransform: 'none', py: 0.5 }}>Side-by-Side</ToggleButton>
            </ToggleButtonGroup>
          </Box>
    
          {/* Label Alignment */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Label Alignment
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.labelAlign || 'left'}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  updateElement(selectedElement.id, { labelAlign: value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="left" sx={{ textTransform: 'none', py: 0.5 }}>Left</ToggleButton>
              <ToggleButton value="center" sx={{ textTransform: 'none', py: 0.5 }}>Center</ToggleButton>
              <ToggleButton value="right" sx={{ textTransform: 'none', py: 0.5 }}>Right</ToggleButton>
            </ToggleButtonGroup>
          </Box>
    
          {/* Stars Alignment */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Stars Alignment
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.starsAlign || (selectedElement.rowLayout === 'side-by-side' ? 'right' : 'left')}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  updateElement(selectedElement.id, { starsAlign: value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="left" sx={{ textTransform: 'none', py: 0.5 }}>Left</ToggleButton>
              <ToggleButton value="center" sx={{ textTransform: 'none', py: 0.5 }}>Center</ToggleButton>
              <ToggleButton value="right" sx={{ textTransform: 'none', py: 0.5 }}>Right</ToggleButton>
            </ToggleButtonGroup>
          </Box>
    
          {/* Rating Text Position */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Rating Text Position
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.ratingTextPosition || 'right'}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  updateElement(selectedElement.id, { ratingTextPosition: value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="left" sx={{ textTransform: 'none', py: 0.5 }}>Left of Stars</ToggleButton>
              <ToggleButton value="right" sx={{ textTransform: 'none', py: 0.5 }}>Right of Stars</ToggleButton>
            </ToggleButtonGroup>
          </Box>
    
          {/* Text Styling Section */}
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            📝 Text Styling
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Choose a preset to apply to all row text (Label & Rating Text)
          </Typography>
    
          <Box sx={{ mb: 3 }}>
            <SimplePresetSelector
              onStyleSelect={(presetName, preset) => {
                console.log('🎨 Star Rating Table preset applied:', presetName, preset);
    
                const updatedStyles = {
                  ...selectedElement.styles,
                  textStylePreset: presetName,
                  fontFamily: preset.fontFamily || selectedElement.styles?.fontFamily,
                  fontWeight: preset.fontWeight || selectedElement.styles?.fontWeight,
                  color: preset.color || selectedElement.styles?.color,
                  labelFontSize: preset.fontSize || selectedElement.styles?.labelFontSize
                };
    
                updateElement(selectedElement.id, { styles: updatedStyles }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              currentStyle={selectedElement.styles?.textStylePreset}
              presetFilter={(presetName) => {
                return !presetName.includes('quotation') && !presetName.includes('bullet') && !presetName.includes('heading');
              }}
            />
          </Box>
    
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                ⭐ Star Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={selectedElement.styles?.starColor || '#ffc107'}
                  onChange={(e) => {
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, starColor: e.target.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }}
                  style={{
                    width: '100%',
                    height: '35px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                📝 Text Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={selectedElement.styles?.color || '#1a1a1a'}
                  onChange={(e) => {
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, color: e.target.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }}
                  style={{
                    width: '100%',
                    height: '35px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                />
              </Box>
            </Box>
          </Box>
    
          {/* Rating Text Alignment */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Rating Text Alignment
            </Typography>
            <ToggleButtonGroup
              value={selectedElement.ratingTextAlign || 'left'}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  updateElement(selectedElement.id, { ratingTextAlign: value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="left" sx={{ textTransform: 'none', py: 0.5 }}>Left</ToggleButton>
              <ToggleButton value="center" sx={{ textTransform: 'none', py: 0.5 }}>Center</ToggleButton>
              <ToggleButton value="right" sx={{ textTransform: 'none', py: 0.5 }}>Right</ToggleButton>
            </ToggleButtonGroup>
          </Box>
    
          {/* Max Stars */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Max Stars: {selectedElement.maxStars || 5}
            </Typography>
            <Slider
              key={`max-stars-${selectedElement.id}`}
              defaultValue={selectedElement.maxStars || 5}
              onChangeCommitted={(e, value) => {
                updateElement(selectedElement.id, { maxStars: value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
              }}
              step={1}
              marks
              min={1}
              max={10}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
    
          {/* Show Title Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={selectedElement.showTitle !== false}
                onChange={(e) => {
                  updateElement(selectedElement.id, { showTitle: e.target.checked }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }}
              />
            }
            label="Show table title"
            sx={{ mb: 1 }}
          />
    
          {/* Show Row Dividers */}
          <FormControlLabel
            control={
              <Switch
                checked={selectedElement.showRowDividers !== false}
                onChange={(e) => {
                  updateElement(selectedElement.id, { showRowDividers: e.target.checked }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }}
              />
            }
            label="Show row dividers"
            sx={{ mb: 1 }}
          />
    
          {/* Divider controls - only visible when dividers are enabled */}
          {selectedElement.showRowDividers !== false && (
            <Box sx={{ ml: 2, mb: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
              {/* Divider Thickness */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Divider Thickness: {selectedElement.dividerThickness || '1px'}
                </Typography>
                <Slider
                  key={`divider-thick-${selectedElement.id}`}
                  defaultValue={parseInt(selectedElement.dividerThickness) || 1}
                  onChangeCommitted={(e, value) => {
                    updateElement(selectedElement.id, { dividerThickness: `${value}px` }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }}
                  step={1}
                  min={1}
                  max={6}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Box>
    
              {/* Divider Color */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Divider Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      backgroundColor: selectedElement.dividerColor || '#eeeeee',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <input
                      type="color"
                      value={selectedElement.dividerColor || '#eeeeee'}
                      onChange={(e) => {
                        updateElement(selectedElement.id, { dividerColor: e.target.value }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                      }}
                      style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%', height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {selectedElement.dividerColor || '#eeeeee'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    
      {/* Background Color Section - Show for text, heading, button elements, layouts, and specific sub-elements */}
      {(selectedElement.type === ELEMENT_TYPES.TEXT ||
        selectedElement.type === ELEMENT_TYPES.HEADING ||
        selectedElement.type === ELEMENT_TYPES.QUOTATION_BLOCK ||
        selectedElement.type === ELEMENT_TYPES.BUTTON ||
        selectedElement.type === ELEMENT_TYPES.LAYOUT ||
        selectedElement.type === ELEMENT_TYPES.CUSTOM_LIST_TWO_LINE ||
        (selectedElement.type === 'BUTTON_WITH_NOTICE' && (selectedSubElement === 'notice' || selectedSubElement === 'callToAction' || selectedSubElement === 'button')) ||
        selectedElement.type === 'BUTTON_IMAGE_CALLOUT' ||
        selectedElement.type === 'BUTTON_TEXT_VARIANT' ||
        selectedElement.type === 'BUTTON_HEADLINE_PRODUCT_IMAGE' ||
        selectedElement.type === ELEMENT_TYPES.STAR_RATING_TABLE) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Background Color
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose a preset color or enter a custom value
            </Typography>
    
            {/* Enhanced Color Palette */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${isBrandsPage ? 4 : 5}, 1fr)`, gap: 1, mb: 2 }}>
              {getInlineBackgroundColors().map((color) => {
                const currentBgColor = selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                  ? selectedElement.subElementStyles?.[selectedSubElement]?.backgroundColor || 'transparent'
                  : selectedElement.styles?.backgroundColor || 'transparent';
    
                const isSelected = currentBgColor === color.value;
    
                return (
                  <Box
                    key={color.name}
                    onClick={() => {
                      // Handle sub-element styling for composite elements
                      if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                        const subElementStyles = selectedElement.subElementStyles || {};
                        const updatedSubElementStyles = {
                          ...subElementStyles,
                          [selectedSubElement]: {
                            ...subElementStyles[selectedSubElement],
                            backgroundColor: color.value
                          }
                        };
                        updateElement(selectedElement.id, {
                          subElementStyles: updatedSubElementStyles
                        }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                      } else {
                        // Regular element styling
                        updateElement(selectedElement.id, {
                          styles: {
                            ...selectedElement.styles,
                            backgroundColor: color.value,
                            background: color.value // Also update 'background' to override gradients
                          }
                        }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                      }
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: color.value === 'transparent' ? '#ffffff' : color.value,
                      border: color.value === 'transparent' ? '2px dashed #ccc' : `2px solid ${isSelected ? '#1976d2' : '#e0e0e0'}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        borderColor: '#1976d2',
                        zIndex: 1
                      },
                      ...(isSelected && {
                        transform: 'scale(1.1)',
                        boxShadow: '0 0 0 2px #1976d2'
                      })
                    }}
                    title={color.name}
                  >
                    {color.value === 'transparent' && (
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '10px',
                        color: '#666',
                        fontWeight: 'bold'
                      }}>
                        ∅
                      </Box>
                    )}
                    {isSelected && (
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: color.value === '#ffffff' || color.value === 'transparent' ? '#1976d2' : '#ffffff',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
    
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <input
                type="color"
                value={(() => {
                  const bgColor = selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.backgroundColor || 'transparent'
                    : selectedElement.styles?.backgroundColor || 'transparent';
                  // Convert common CSS colors to hex for color picker
                  if (bgColor === 'transparent' || bgColor === 'none') return '#ffffff';
                  if (bgColor.startsWith('#')) return bgColor;
                  // For named colors, return a default
                  return '#ffffff';
                })()}
                onChange={(e) => {
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        backgroundColor: e.target.value
                      }
                    };
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  } else {
                    // Regular element styling
                    updateElement(selectedElement.id, {
                      styles: {
                        ...selectedElement.styles,
                        backgroundColor: e.target.value,
                        background: e.target.value // Also update 'background' to override gradients
                      }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
                }}
                style={{
                  width: '50px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Pick a color"
              />
              <TextField
                fullWidth
                label="Custom Background Color"
                value={
                  selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.backgroundColor || 'transparent'
                    : selectedElement.styles?.backgroundColor || 'transparent'
                }
                onChange={(e) => {
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        backgroundColor: e.target.value
                      }
                    };
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  } else {
                    // Regular element styling
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, backgroundColor: e.target.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
                }}
                placeholder="e.g., #ffffff, rgb(255,255,255), or transparent"
                helperText="Use color picker or enter any valid CSS color value"
                size="small"
              />
            </Box>
          </Box>
        )}
    
      {/* Text Color Section - Show for text, heading, button elements, and specific sub-elements */}
      {(selectedElement.type === ELEMENT_TYPES.TEXT ||
        selectedElement.type === ELEMENT_TYPES.HEADING ||
        selectedElement.type === ELEMENT_TYPES.STAR_RATING ||
        selectedElement.type === ELEMENT_TYPES.QUOTATION_BLOCK ||
        selectedElement.type === ELEMENT_TYPES.BUTTON ||
        (selectedElement.type === 'BUTTON_WITH_NOTICE' && (selectedSubElement === 'notice' || selectedSubElement === 'callToAction' || selectedSubElement === 'button')) ||
        selectedElement.type === 'BUTTON_IMAGE_CALLOUT' ||
        selectedElement.type === 'BUTTON_TEXT_VARIANT' ||
        selectedElement.type === ELEMENT_TYPES.STAR_RATING_TABLE) && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Text Color
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Choose a preset color or enter a custom value
            </Typography>
    
            {/* Enhanced Text Color Palette */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${isBrandsPage ? 4 : 5}, 1fr)`, gap: 1, mb: 1.5 }}>
              {getInlineTextColors().map((color) => {
                const currentTextColor = selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                  ? selectedElement.subElementStyles?.[selectedSubElement]?.color || '#101022'
                  : selectedElement.styles?.color || '#101022';
    
                const isSelected = currentTextColor === color.value;
    
                return (
                  <Box
                    key={color.name}
                    onClick={() => {
                      // Handle sub-element styling for composite elements
                      if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                        const subElementStyles = selectedElement.subElementStyles || {};
                        const updatedSubElementStyles = {
                          ...subElementStyles,
                          [selectedSubElement]: {
                            ...subElementStyles[selectedSubElement],
                            color: color.value
                          }
                        };
                        updateElement(selectedElement.id, {
                          subElementStyles: updatedSubElementStyles
                        }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                      } else {
                        // Regular element styling
                        updateElement(selectedElement.id, {
                          styles: { ...selectedElement.styles, color: color.value }
                        }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                      }
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: color.value,
                      border: `2px solid ${isSelected ? '#1976d2' : '#e0e0e0'}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        borderColor: '#1976d2',
                        zIndex: 1
                      },
                      ...(isSelected && {
                        transform: 'scale(1.1)',
                        boxShadow: '0 0 0 2px #1976d2'
                      })
                    }}
                    title={color.name}
                  >
                    {isSelected && (
                      <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: color.value === '#ffffff' || color.value === '#e7cb16' || color.value === '#fbc02d' ? '#101022' : '#ffffff',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
    
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <input
                type="color"
                value={(() => {
                  const textColor = selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.color || '#101022'
                    : selectedElement.styles?.color || '#101022';
                  // Convert common CSS colors to hex for color picker
                  if (textColor.startsWith('#')) return textColor;
                  // For named colors, return a default
                  return '#101022';
                })()}
                onChange={(e) => {
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        color: e.target.value
                      }
                    };
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  } else {
                    // Regular element styling
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, color: e.target.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
                }}
                style={{
                  width: '50px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                title="Pick a text color"
              />
              <TextField
                fullWidth
                label="Custom Text Color"
                value={
                  selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.color || '#101022'
                    : selectedElement.styles?.color || '#101022'
                }
                onChange={(e) => {
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        color: e.target.value
                      }
                    };
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  } else {
                    // Regular element styling
                    updateElement(selectedElement.id, {
                      styles: { ...selectedElement.styles, color: e.target.value }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
                }}
                placeholder="e.g., #101022, rgb(0,0,0), or black"
                helperText="Use color picker or enter any valid CSS color value"
                size="small"
              />
            </Box>
          </Box>
        )}
    
      {/* Typography Section - Show for text, heading, button elements, and specific sub-elements */}
      {(selectedElement.type === ELEMENT_TYPES.TEXT ||
        selectedElement.type === ELEMENT_TYPES.HEADING ||
        selectedElement.type === ELEMENT_TYPES.STAR_RATING ||
        selectedElement.type === ELEMENT_TYPES.QUOTATION_BLOCK ||
        selectedElement.type === ELEMENT_TYPES.BUTTON ||
        (selectedElement.type === 'BUTTON_WITH_NOTICE' && (selectedSubElement === 'notice' || selectedSubElement === 'callToAction' || selectedSubElement === 'button')) ||
        selectedElement.type === 'BUTTON_IMAGE_CALLOUT' ||
        selectedElement.type === 'BUTTON_TEXT_VARIANT') && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Typography
            </Typography>
    
            {/* Font Family and Font Size - Hide for text, button, heading, and custom list elements */}
            {selectedElement.type !== ELEMENT_TYPES.TEXT && selectedElement.type !== ELEMENT_TYPES.BUTTON && selectedElement.type !== ELEMENT_TYPES.HEADING && selectedElement.type !== ELEMENT_TYPES.CUSTOM_LIST && (
              <>
                <TextField
                  key={`font-family-3-${selectedElement?.id}-${selectedElement?.styles?.fontFamily || 'empty'}`}
                  fullWidth
                  label="Font Family"
                  select
                  value={getCurrentFontFamily(selectedElement, selectedSubElement)}
                  onFocus={() => {
                    console.log('🎯 Select Font Family focused. Value:', getCurrentFontFamily(selectedElement, selectedSubElement));
                    console.log('🎯 Select Font Family full styles:', selectedElement.styles);
                  }}
                  onChange={(e) => {
                    // Handle sub-element styling for composite elements
                    if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                      const subElementStyles = selectedElement.subElementStyles || {};
                      const updatedSubElementStyles = {
                        ...subElementStyles,
                        [selectedSubElement]: {
                          ...subElementStyles[selectedSubElement],
                          fontFamily: e.target.value
                        }
                      };
                      updateElement(selectedElement.id, {
                        subElementStyles: updatedSubElementStyles
                      }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                    } else {
                      // Regular element styling
                      updateElement(selectedElement.id, {
                        styles: { ...selectedElement.styles, fontFamily: e.target.value }
                      }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                    }
                  }}
                  sx={{ mb: 1.5 }}
                >
                  <MenuItem value="inherit">Default</MenuItem>
                  {getPresetFontFamilies().map((fontFamily) => (
                    <MenuItem key={fontFamily} value={fontFamily}>
                      {fontFamily.replace(/['"]/g, '').replace(', sans-serif', '').replace(', serif', '').replace(', monospace', '')}
                    </MenuItem>
                  ))}
                </TextField>
    
                <TextField
                  fullWidth
                  label="Font Size"
                  select
                  value={
                    selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                      ? selectedElement.subElementStyles?.[selectedSubElement]?.fontSize || (selectedElement.type === ELEMENT_TYPES.HEADING ? '24px' : '18px')
                      : selectedElement.styles?.fontSize || (selectedElement.type === ELEMENT_TYPES.HEADING ? '24px' : '18px')
                  }
                  onChange={(e) => {
                    // Handle sub-element styling for composite elements
                    if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                      const subElementStyles = selectedElement.subElementStyles || {};
                      const updatedSubElementStyles = {
                        ...subElementStyles,
                        [selectedSubElement]: {
                          ...subElementStyles[selectedSubElement],
                          fontSize: e.target.value
                        }
                      };
                      updateElement(selectedElement.id, {
                        subElementStyles: updatedSubElementStyles
                      }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                    } else {
                      // Regular element styling
                      updateElement(selectedElement.id, {
                        styles: { ...selectedElement.styles, fontSize: e.target.value }
                      }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                    }
                  }}
                  sx={{ mb: 1.5 }}
                >
                  <MenuItem value="12px">12px - Small</MenuItem>
                  <MenuItem value="14px">14px - Small+</MenuItem>
                  <MenuItem value="16px">16px - Medium</MenuItem>
                  <MenuItem value="18px">18px - Medium+</MenuItem>
                  <MenuItem value="20px">20px - Large</MenuItem>
                  <MenuItem value="22px">22px - Large+</MenuItem>
                  <MenuItem value="24px">24px - Extra Large</MenuItem>
                  <MenuItem value="28px">28px - Extra Large+</MenuItem>
                  <MenuItem value="32px">32px - Huge</MenuItem>
                </TextField>
              </>
            )}
    
            <TextField
              fullWidth
              label="Text Transform"
              select
              value={
                selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                  ? selectedElement.subElementStyles?.[selectedSubElement]?.textTransform || 'none'
                  : selectedElement.styles?.textTransform || 'none'
              }
              onChange={(e) => {
                // Handle sub-element styling for composite elements
                if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                  const subElementStyles = selectedElement.subElementStyles || {};
                  const updatedSubElementStyles = {
                    ...subElementStyles,
                    [selectedSubElement]: {
                      ...subElementStyles[selectedSubElement],
                      textTransform: e.target.value
                    }
                  };
                  updateElement(selectedElement.id, {
                    subElementStyles: updatedSubElementStyles
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                } else {
                  // Regular element styling
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, textTransform: e.target.value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              sx={{ mb: 1.5 }}
            >
              <MenuItem value="none">Normal</MenuItem>
              <MenuItem value="uppercase">ALL CAPITAL</MenuItem>
              <MenuItem value="capitalize">First Letter Capital</MenuItem>
              <MenuItem value="titlecase">Title Case</MenuItem>
              <MenuItem value="lowercase">all lowercase</MenuItem>
            </TextField>
    
            <TextField
              fullWidth
              label="Font Weight"
              select
              value={
                selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                  ? selectedElement.subElementStyles?.[selectedSubElement]?.fontWeight || 400
                  : selectedElement.styles?.fontWeight || 400
              }
              onChange={(e) => {
                // Handle sub-element styling for composite elements
                if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                  const subElementStyles = selectedElement.subElementStyles || {};
                  const updatedSubElementStyles = {
                    ...subElementStyles,
                    [selectedSubElement]: {
                      ...subElementStyles[selectedSubElement],
                      fontWeight: e.target.value
                    }
                  };
                  console.log('🎨 Updating sub-element styles:', updatedSubElementStyles);
                  updateElement(selectedElement.id, {
                    subElementStyles: updatedSubElementStyles
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                } else {
                  // Regular element styling
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, fontWeight: e.target.value }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              sx={{ mb: 1.5 }}
            >
              <MenuItem value={400}>Normal</MenuItem>
              <MenuItem value={100}>Lighter</MenuItem>
              <MenuItem value={900}>Bold</MenuItem>
            </TextField>
    
            {selectedElement.type !== ELEMENT_TYPES.BUTTON && (
              <TextField
                fullWidth
                label="Text Align"
                select
                value={
                  selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.textAlign || 'left'
                    : selectedElement.styles?.textAlign || 'left'
                }
                onChange={(e) => {
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        textAlign: e.target.value
                      }
                    };
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  } else {
                    // Regular element styling
                    const newStyles = { ...selectedElement.styles, textAlign: e.target.value };
                    // For STAR_RATING, also sync justifyContent so the group aligns correctly
                    if (selectedElement.type === ELEMENT_TYPES.STAR_RATING) {
                      newStyles.justifyContent = e.target.value === 'left' ? 'flex-start' : e.target.value === 'right' ? 'flex-end' : 'center';
                    }
                    updateElement(selectedElement.id, {
                      styles: newStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
                }}
                sx={{ mb: 1.5 }}
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
                <MenuItem value="justify">Justify</MenuItem>
              </TextField>
            )}
          </Box>
        )}
    
      {/* Spacing Section - Show for all elements except buttons, text, heading, and video elements */}
      {selectedElement.type !== ELEMENT_TYPES.BUTTON &&
        selectedElement.type !== ELEMENT_TYPES.TEXT &&
        selectedElement.type !== ELEMENT_TYPES.HEADING &&
        selectedElement.type !== ELEMENT_TYPES.VIDEO &&
        selectedElement.type !== ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Spacing & Layout
            </Typography>
    
            <TextField
              fullWidth
              label="Padding"
              defaultValue={
                selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                  ? selectedElement.subElementStyles?.[selectedSubElement]?.padding || '8px'
                  : selectedElement.styles?.padding || '8px'
              }
              onChange={(e) => {
                // Only update on blur to prevent focus loss
              }}
              onBlur={(e) => {
                const newValue = e.target.value;
                console.log('🔍 Padding field onBlur - newValue:', newValue);
                console.log('🔍 Padding field onBlur - selectedElement.type:', selectedElement.type);
                console.log('🔍 Padding field onBlur - selectedElement.styles before:', selectedElement.styles);
    
                if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                  const subElementStyles = selectedElement.subElementStyles || {};
                  const updatedSubElementStyles = {
                    ...subElementStyles,
                    [selectedSubElement]: {
                      ...subElementStyles[selectedSubElement],
                      padding: newValue
                    }
                  };
                  updateElement(selectedElement.id, {
                    subElementStyles: updatedSubElementStyles
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                } else {
                  updateElement(selectedElement.id, {
                    styles: { ...selectedElement.styles, padding: newValue }
                  }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                }
              }}
              placeholder="e.g., 8px, 10px 15px, 5px 10px 15px 20px"
              helperText="Space inside the element"
              sx={{ mb: 1.5 }}
            />
    
          </Box>
        )}
    
      {/* Border Section - Show for all elements except video elements */}
      {selectedElement.type !== ELEMENT_TYPES.VIDEO &&
        selectedElement.type !== ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Border & Shape
            </Typography>
    
            <FormControl fullWidth sx={{ mb: 1.5 }}>
              <InputLabel>Border</InputLabel>
              <Select
                value={
                  selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.border || 'none'
                    : selectedElement.styles?.border || 'none'
                }
                label="Border"
                onOpen={() => console.log('🎨 Select opened')}
                onClose={() => console.log('🎨 Select closed')}
                onClick={() => console.log('🎨 Select clicked')}
                onChange={(e) => {
                  console.log('🎨🎨🎨 BORDER CHANGE TRIGGERED! 🎨🎨🎨');
                  console.log('🎨 Event object:', e);
                  console.log('🎨 Event target:', e.target);
                  console.log('🎨 New value:', e.target.value);
                  console.log('🎨 Selected element:', selectedElement);
                  console.log('🎨 Selected element type:', selectedElement?.type);
                  console.log('🎨 Selected sub element:', selectedSubElement);
                  console.log('🎨 Selected element context:', selectedElementContext);
    
                  const borderValue = e.target.value;
                  console.log('🎨 Setting border to:', borderValue);
    
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    console.log('🎨 Using sub-element styling path');
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        border: borderValue
                      }
                    };
                    console.log('🎨 Updated sub-element styles:', updatedSubElementStyles);
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
    
                    // Force aggressive re-render for sub-element border changes
                    console.log('🎨 Forcing aggressive re-render for sub-element border change');
                    setTimeout(() => {
                      setForceRender(prev => {
                        const newValue = prev + 1;
                        console.log('🎨 Sub-element border forceRender updated from', prev, 'to', newValue);
                        return newValue;
                      });
                    }, 50);
                  } else {
                    // Regular element styling
                    console.log('🎨 Using regular element styling path');
                    console.log('🎨 Current element styles:', selectedElement.styles);
                    const newStyles = { ...selectedElement.styles, border: borderValue };
                    console.log('🎨 New element styles:', newStyles);
                    updateElement(selectedElement.id, {
                      styles: newStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
    
                    // Force aggressive re-render for border changes
                    console.log('🎨 Forcing aggressive re-render for border change');
                    setTimeout(() => {
                      setForceRender(prev => {
                        const newValue = prev + 1;
                        console.log('🎨 Border forceRender updated from', prev, 'to', newValue);
                        return newValue;
                      });
                    }, 50);
                  }
    
                  console.log('🎨🎨🎨 BORDER CHANGE COMPLETED! 🎨🎨🎨');
                }}
              >
                <MenuItem value="none">No Border</MenuItem>
                <MenuItem value="1px dotted #101022">1px Dotted Black</MenuItem>
                <MenuItem value="1px dotted #ff0000">1px Dotted Red</MenuItem>
                <MenuItem value="1px dotted #808080">1px Dotted Grey</MenuItem>
              </Select>
            </FormControl>
    
            {selectedElement.type !== ELEMENT_TYPES.BUTTON && (
              <TextField
                fullWidth
                label="Border Radius"
                select
                value={
                  selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE'
                    ? selectedElement.subElementStyles?.[selectedSubElement]?.borderRadius || '0px'
                    : selectedElement.styles?.borderRadius || '0px'
                }
                onChange={(e) => {
                  console.log('🔄🔄🔄 BORDER RADIUS CHANGE TRIGGERED! 🔄🔄🔄');
                  console.log('🔄 New border radius value:', e.target.value);
                  console.log('🔄 Selected element:', selectedElement);
    
                  // Handle sub-element styling for composite elements
                  if (selectedSubElement && selectedElement.type === 'BUTTON_WITH_NOTICE') {
                    console.log('🔄 Using sub-element styling path');
                    const subElementStyles = selectedElement.subElementStyles || {};
                    const updatedSubElementStyles = {
                      ...subElementStyles,
                      [selectedSubElement]: {
                        ...subElementStyles[selectedSubElement],
                        borderRadius: e.target.value
                      }
                    };
                    console.log('🔄 Updated sub-element styles:', updatedSubElementStyles);
                    updateElement(selectedElement.id, {
                      subElementStyles: updatedSubElementStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  } else {
                    // Regular element styling
                    console.log('🔄 Using regular element styling path');
                    console.log('🔄 Current element styles:', selectedElement.styles);
                    const newStyles = { ...selectedElement.styles, borderRadius: e.target.value };
                    console.log('🔄 New element styles:', newStyles);
                    updateElement(selectedElement.id, {
                      styles: newStyles
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
    
                  console.log('🔄🔄🔄 BORDER RADIUS CHANGE COMPLETED! 🔄🔄🔄');
                }}
                helperText="Choose corner roundness"
                sx={{ mb: 1.5 }}
              >
                <MenuItem value="0px">No Rounded</MenuItem>
                <MenuItem value="12px">Rounded</MenuItem>
              </TextField>
            )}
          </Box>
        )}
    
      {/* Background Image Section - Show only for Layout elements */}
      {(selectedElement.type === ELEMENT_TYPES.LAYOUT) && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Background Image
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Add a background image to your layout. Use 'Cover' or 'Contain' to maintain image aspect ratio.
          </Typography>
    
          <TextField
            fullWidth
            label="Background Image URL"
            value={selectedElement.styles?.backgroundImage?.replace('url(', '').replace(')', '') || ''}
            onChange={(e) => {
              const imageUrl = e.target.value;
              updateElement(selectedElement.id, {
                styles: {
                  ...selectedElement.styles,
                  backgroundImage: imageUrl ? `url(${imageUrl})` : 'none'
                }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
            }}
            placeholder="https://example.com/image.jpg"
            helperText="Enter image URL or upload a file below"
            sx={{ mb: 1.5 }}
          />
    
          {/* File Upload for Background Image */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const imageUrl = await handleImageUpload(file, selectedElement.id, true);
                  if (imageUrl) {
                    updateElement(selectedElement.id, {
                      styles: {
                        ...selectedElement.styles,
                        backgroundImage: `url(${imageUrl})`
                      }
                    }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId);
                  }
                }
              }}
              style={{ display: 'none' }}
              id={`layout-bg-upload-${selectedElement.id}`}
            />
            <label htmlFor={`layout-bg-upload-${selectedElement.id}`}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                disabled={imageUploading && uploadingElementId === selectedElement.id}
                sx={{ minWidth: 120 }}
              >
                {imageUploading && uploadingElementId === selectedElement.id ? 'Uploading...' : 'Upload Background'}
              </Button>
            </label>
            {imageUploading && uploadingElementId === selectedElement.id && (
              <Box sx={{ ml: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
    
          {/* Background Image Options */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Background Size</InputLabel>
              <Select
                value={selectedElement.styles?.backgroundSize || 'cover'}
                onChange={(e) => updateElement(selectedElement.id, {
                  styles: { ...selectedElement.styles, backgroundSize: e.target.value }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
                label="Background Size"
              >
                <MenuItem value="cover">Cover (Keep Aspect Ratio)</MenuItem>
                <MenuItem value="contain">Contain (Keep Aspect Ratio)</MenuItem>
                <MenuItem value="auto">Auto (Original Size)</MenuItem>
                <MenuItem value="100% 100%">Stretch (Distort Image)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Cover: Fills layout, may crop image • Contain: Fits entire image, may show background
              </Typography>
            </FormControl>
    
            <FormControl fullWidth size="small">
              <InputLabel>Background Position</InputLabel>
              <Select
                value={selectedElement.styles?.backgroundPosition || 'center'}
                onChange={(e) => updateElement(selectedElement.id, {
                  styles: { ...selectedElement.styles, backgroundPosition: e.target.value }
                }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
                label="Background Position"
              >
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="top">Top</MenuItem>
                <MenuItem value="bottom">Bottom</MenuItem>
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="right">Right</MenuItem>
                <MenuItem value="top left">Top Left</MenuItem>
                <MenuItem value="top right">Top Right</MenuItem>
                <MenuItem value="bottom left">Bottom Left</MenuItem>
                <MenuItem value="bottom right">Bottom Right</MenuItem>
              </Select>
            </FormControl>
          </Box>
    
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Background Repeat</InputLabel>
            <Select
              value={selectedElement.styles?.backgroundRepeat || 'no-repeat'}
              onChange={(e) => updateElement(selectedElement.id, {
                styles: { ...selectedElement.styles, backgroundRepeat: e.target.value }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
              label="Background Repeat"
            >
              <MenuItem value="no-repeat">No Repeat</MenuItem>
              <MenuItem value="repeat">Repeat</MenuItem>
              <MenuItem value="repeat-x">Repeat X</MenuItem>
              <MenuItem value="repeat-y">Repeat Y</MenuItem>
            </Select>
          </FormControl>
    
          {/* Clear Background Image Button */}
          {selectedElement.styles?.backgroundImage && selectedElement.styles?.backgroundImage !== 'none' && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => updateElement(selectedElement.id, {
                styles: {
                  ...selectedElement.styles,
                  backgroundImage: 'none'
                }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
              sx={{ mt: 1 }}
            >
              Remove Background Image
            </Button>
          )}
        </Box>
      )}
    
    
      {/* Image-specific styling options */}
      {selectedElement.type === ELEMENT_TYPES.IMAGE && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Image Specific
          </Typography>
    
          <TextField
            fullWidth
            label="Image Width"
            value={selectedElement.styles?.width || 'auto'}
            onChange={(e) => updateElement(selectedElement.id, {
              styles: { ...selectedElement.styles, width: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            placeholder="e.g., 100px, 50%, auto"
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Image Height"
            value={selectedElement.styles?.height || 'auto'}
            onChange={(e) => updateElement(selectedElement.id, {
              styles: { ...selectedElement.styles, height: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            placeholder="e.g., 100px, 50%, auto"
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Object Fit"
            select
            value={selectedElement.styles?.objectFit || 'cover'}
            onChange={(e) => updateElement(selectedElement.id, {
              styles: { ...selectedElement.styles, objectFit: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value="cover">Cover</MenuItem>
            <MenuItem value="contain">Contain</MenuItem>
            <MenuItem value="fill">Fill</MenuItem>
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="scale-down">Scale Down</MenuItem>
          </TextField>
        </Box>
      )}
    
      {selectedElement.type === ELEMENT_TYPES.IMAGE_OVERLAY && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Image Overlay Settings
          </Typography>
    
    
    
          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
            Overlay Box Position (e.g., 20px, 5%, 0)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
            <TextField
              label="Top"
              size="small"
              value={selectedElement.overlayStyles?.top || ''}
              onChange={(e) => updateElement(selectedElement.id, {
                overlayStyles: {
                  ...selectedElement.overlayStyles,
                  top: e.target.value,
                  bottom: e.target.value ? 'auto' : selectedElement.overlayStyles?.bottom
                }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            />
            <TextField
              label="Bottom"
              size="small"
              value={selectedElement.overlayStyles?.bottom || ''}
              onChange={(e) => updateElement(selectedElement.id, {
                overlayStyles: {
                  ...selectedElement.overlayStyles,
                  bottom: e.target.value,
                  top: e.target.value ? 'auto' : selectedElement.overlayStyles?.top
                }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            />
            <TextField
              label="Left"
              size="small"
              value={selectedElement.overlayStyles?.left || ''}
              onChange={(e) => updateElement(selectedElement.id, {
                overlayStyles: {
                  ...selectedElement.overlayStyles,
                  left: e.target.value,
                  right: e.target.value ? 'auto' : selectedElement.overlayStyles?.right
                }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            />
            <TextField
              label="Right"
              size="small"
              value={selectedElement.overlayStyles?.right || ''}
              onChange={(e) => updateElement(selectedElement.id, {
                overlayStyles: {
                  ...selectedElement.overlayStyles,
                  right: e.target.value,
                  left: e.target.value ? 'auto' : selectedElement.overlayStyles?.left
                }
              }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            />
          </Box>
    
          <TextField
            fullWidth
            label="Overlay Box Background"
            value={selectedElement.overlayStyles?.backgroundColor || '#ffffff'}
            onChange={(e) => updateElement(selectedElement.id, {
              overlayStyles: { ...selectedElement.overlayStyles, backgroundColor: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Overlay Box Width"
            value={selectedElement.overlayStyles?.width || 'auto'}
            onChange={(e) => updateElement(selectedElement.id, {
              overlayStyles: { ...selectedElement.overlayStyles, width: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            placeholder="e.g., 200px, 50%, auto"
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Overlay Box Padding"
            value={selectedElement.overlayStyles?.padding || '20px'}
            onChange={(e) => updateElement(selectedElement.id, {
              overlayStyles: { ...selectedElement.overlayStyles, padding: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Overlay Box Border Radius"
            value={selectedElement.overlayStyles?.borderRadius || '8px'}
            onChange={(e) => updateElement(selectedElement.id, {
              overlayStyles: { ...selectedElement.overlayStyles, borderRadius: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            sx={{ mb: 1.5 }}
          />
        </Box>
      )}
    
      {/* Croppable Image-specific styling options */}
      {selectedElement.type === ELEMENT_TYPES.CROPPABLE_IMAGE && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Croppable Image Specific
          </Typography>
    
          <TextField
            fullWidth
            label="Image Width (px)"
            type="number"
            value={selectedElement.styles?.width || 300}
            onChange={(e) => updateElement(selectedElement.id, {
              styles: { ...selectedElement.styles, width: parseInt(e.target.value) || 300 }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Image Height (px)"
            type="number"
            value={selectedElement.styles?.height || 200}
            onChange={(e) => updateElement(selectedElement.id, {
              styles: { ...selectedElement.styles, height: parseInt(e.target.value) || 200 }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            sx={{ mb: 1.5 }}
          />
    
          <TextField
            fullWidth
            label="Border Radius"
            value={selectedElement.styles?.borderRadius || '8px'}
            onChange={(e) => updateElement(selectedElement.id, {
              styles: { ...selectedElement.styles, borderRadius: e.target.value }
            }, selectedElementContext?.isInZone, selectedElementContext?.layoutId, selectedElementContext?.zoneId)}
            placeholder="e.g., 8px, 50%, 0"
            sx={{ mb: 1.5 }}
          />
    
    
        </Box>
      )}
    </Box>
  );
};

export default StyleTab;
